/* ---------------------------------------------------------------------------- */
/*                              DEVICE AND EQUIPMENT                            */
/* ---------------------------------------------------------------------------- */

/* ------ */
/* Device */
/* ------ */
export const deviceFragment = `
fragment deviceFragment on Device {
  disabled,
  id,
  name,
  serial,
  timeout,
  lastCommDate,
  }
`;

/* --------- */
/* Operation */
/* --------- */
export const operationFragment = `
  fragment operationFragment on Operation {
    devices {
      device {
        ...deviceFragment
      },
      error {
        code,
        desc,
        ignored
      },
      status
    },
    id,
    status
  }

  ${deviceFragment}
`;

/* --------- */
/* Equipment */
/* --------- */
export const equipmentFragment = `
  fragment equipmentFragment on Equipment {
    id,
    name,
    type,
    cameraStatus,
  }
`;
// 🔽
/* -------------- */
/* Door equipment */
/* -------------- */
const doorEquipmentFragment = `
    fragment doorEquipmentFragment on DoorEquipment {
      equipment {
        ...equipmentFragment
      },
      usage
    }

    ${equipmentFragment}
  `;
// 🔽
/* ---- */
/* Door */
/* ---- */
export const doorFragment = `
  fragment doorFragment on Door {
    equipments {
      ...doorEquipmentFragment
    },
    device {
      id
    },
    id,
    name,
    type,
    usage
  }

  ${doorEquipmentFragment}
`;

/* PaymentReader */
export const paymentReaderFragment = `
  fragment paymentReaderFragment on PaymentReader {
    chargingStatus,
    connectionStatus,
    creationDate,
    firmwareVersion,
    id,
    serialNumber,
  }
`;

/* Terminal */
export const terminalFragment = `
  fragment terminalFragment on Terminal {
    appStatus,
    appVersion,
    batteryLevel,
    chargingStatus,
    creationDate,
    id,
    kioskSecret,
    lastCommDate,
    paymentReaders {
      ...paymentReaderFragment
    },
    terminalIdentifier,
    timeout,
    type,
  }

  ${paymentReaderFragment}
`;

/* ---------------------------------------------------------------------------- */
/*                                   METRICS                                    */
/* ---------------------------------------------------------------------------- */

/* ------------------- */
/* Live occupancy rate */
/* ------------------- */
export const metricLiveOccupancyRateFragment = `
  fragment metricLiveOccupancyRateFragment on LiveOccupancyRate {
    occupancy,
    capacity,
    rate
  }
`;

/* --------------------- */
/* Period occupancy rate */
/* --------------------- */
export const metricOccupancyCountFragment = `
  fragment metricOccupancyCountFragment on OccupancyCountOverPeriod {
    average,
    max,
    min
  }
`;

export const metricPeriodOccupancyRateFragment = `
  fragment metricPeriodOccupancyRateFragment on PeriodOccupancyRate {
    date,
    rate {
      average,
      max,
      min
    }
  }
`;

export const metricPredictedOccupancyFragment = `
  fragment metricPredictedOccupancyFragment on PredictedOccupancyPeriod {
    date,
    rate {
      average,
      max,
      min
    },
    count {
      ...metricOccupancyCountFragment
    }
  }

  ${metricOccupancyCountFragment}
`;

/* -------- */
/* Accesses */
/* -------- */
export const metricAccessesFragment = `
  fragment metricAccessesFragment on AccessesMetric {
    date,
    quantities {
      input,
      output,
      na
    }
  }
`;

/* -------- */
/* Revenues */
/* -------- */
export const metricRevenuesFragment = `
  fragment metricRevenuesFragment on RevenuesMetric {
    date,
    value {
      amountInclTax,
      amountExclTax
    }
  }
`;

export const metricsRevenuesComparisonFragment = `
  fragment metricsRevenuesComparisonFragment on RevenuesMetricCompleteSummary {
    daily {
      current { amountExclTax }
      evolutionRatio { amountExclTax },
      previous { amountExclTax }
    },
    monthly {
      current { amountExclTax },
      evolutionRatio { amountExclTax },
      previous { amountExclTax }
    }
    yearly {
      current { amountExclTax },
      evolutionRatio { amountExclTax },
      previous { amountExclTax }
    }
  }
`;

export const generateInvoiceExportFragment = `
  fragment generateInvoiceExportFragment on Download {
    uri
  }
`;

/* ---------------------------------------------------------------------------- */
/*                            OFFER AND PAYMENT                                 */
/* ---------------------------------------------------------------------------- */

/* ---------------- */
/* Service fee rule */
/* ---------------- */
export const serviceFeeRuleFragment = `
  fragment serviceFeeRuleFragment on ServiceFeeRule {
    id
    maximumAmount
    minimumAmount
    name
    priceThreshold
    value
  }
`;

/* ---------- */
/* Price Rule */
/* ---------- */
export const priceRuleFragment = `
  fragment priceRuleFragment on PriceRule {
    currency,
    id,
    taxRate
  }
`;
// 🔽
/* ---------- */
/* Price grid */
/* ---------- */
export const priceGridFragment = `
  fragment priceGridFragment on PriceGrid {
    active,
    additional { amountInclTax },
    id,
    name,
    parkingCategoriesAssociations {
      discount,
      calculationMode,
      parkingCategory {
        id
      }
    },
    priceRule {
      ...priceRuleFragment
    }
    tiers {
      amountInclTax
      duration,
      unit
    }
  }

  ${priceRuleFragment}
`;

/* ----- */
/* Offer */
/* ----- */
export const offerFragment = ({ withCustomer, withParkingCategory }) => `
  fragment offerFragment on Offer {
    amountExclTax
    amountInclTax
    begin,
  ${withCustomer ? (`
    customer {
      id,
    },
  `) : ''}
    discount
    discountedAmountExclTax
    discountedAmountInclTax
    end,
    expiresAt,
  ${withParkingCategory ? (`
    parkingCategory {
        id
    },
  `) : ''}
    priceRule {
      ...priceRuleFragment
    }
    reference,
    spacesQuantity,
    status,
    priceType,
    serviceFeeAmountExclTax
    serviceFeeAmountInclTax
    totalAmountExclTax
    totalAmountInclTax
    type,
  }

  ${priceRuleFragment}
  `;

/* ----------------------- */
/* Offer for added booking */
/* ----------------------- */
export const addedBookingOfferFragment = `
  fragment addedBookingOfferFragment on Offer {
    totalAmountInclTax
    priceType
  }
`;

/* ------------ */
/* Search offer */
/* ------------ */
export const searchOfferFragment = `
  fragment searchOfferFragment on Offer {
    amountExclTax
    amountInclTax
    begin
    discount
    discountedAmountExclTax
    discountedAmountInclTax
    end
    expiresAt
    priceRule {
      ...priceRuleFragment
    }
    priceType
    reference
    spacesQuantity
    status
    serviceFeeAmountExclTax
    serviceFeeAmountInclTax
    totalAmountExclTax
    totalAmountInclTax
    type
  }

  ${priceRuleFragment}
`;

/* ------- */
/* Payment */
/* ------- */
export const paymentFragment = `
  fragment paymentFragment on Payment {
    amount,
    date,
    provider,
    refundedAmount,
    status,
    stripeDetails {
      clientSecret,
      errors {
        code,
        date,
        message
      }
    },
    target,
  }
`;

/* ------- */
/* Invoice */
/* ------- */
export const invoiceFragment = `
  fragment invoiceFragment on Invoice {
    amountExclTax,
    amountInclTax,
    canBeUpdated,
    customerEmail,
    customerFirstname,
    customerLastname,
    date,
    number,
    priceRule {
      ...priceRuleFragment
    },
    serviceFeeAmountInclTax,
    taxAmount,
    type,
  }

  ${priceRuleFragment}
`;

/* -------- */
/* Purchase */
/* -------- */
export const purchaseFragment = `
  fragment purchaseFragment on Purchase {
    amountExclTax,
    amountInclTax,
    discount,
    discountedAmountExclTax,
    discountedAmountInclTax,
    id,
    creationDate,
    offers {
      amountExclTax
      amountInclTax
      begin,
      customer {
        id
      },
      discount
      discountedAmountExclTax
      discountedAmountInclTax
      end,
      expiresAt,
      parking {
        id
      },
      parkingCategory {
        id
      },
      priceRule {
        ...priceRuleFragment
      }
      priceType,
      reference,
      spacesQuantity,
      status,
      totalAmountExclTax
      totalAmountInclTax
      type,
    }
    isRefundable,
    paidAmount,
    refundReason,
    refundInvoice {
      date
      number
    },
    refundedAmount,
    paymentInvoice {
      ...invoiceFragment
    },
    paymentStatus,
    payments {
      ...paymentFragment
    },
    priceRule {
      ...priceRuleFragment
    },
    serviceFeeAmountExclTax,
    serviceFeeAmountInclTax,
    totalAmountExclTax,
    totalAmountInclTax,
    type,
  }

  ${priceRuleFragment}
  ${paymentFragment}
  ${invoiceFragment}
`;

/* --------------------- */
/* Purchase for checkout */
/* --------------------- */
const purchaseForCheckoutFragment = `
  fragment purchaseForCheckoutFragment on Purchase {
    amountExclTax,
    amountInclTax,
    discount,
    discountedAmountExclTax,
    discountedAmountInclTax,
    id,
    creationDate,
    offers {
      amountExclTax
      amountInclTax
      begin,
      discount
      discountedAmountExclTax
      discountedAmountInclTax
      end,
      expiresAt,
      priceRule {
        ...priceRuleFragment
      }
      priceType,
      reference,
      serviceFeeAmountExclTax,
      serviceFeeAmountInclTax,
      spacesQuantity,
      status,
      totalAmountExclTax
      totalAmountInclTax
      type,
    }
    paidAmount,
    paymentInvoice {
      ...invoiceFragment
    },
    paymentStatus,
    payments {
      ...paymentFragment
    },
    priceRule {
      ...priceRuleFragment
    },
    serviceFeeAmountExclTax,
    serviceFeeAmountInclTax,
    totalAmountExclTax,
    totalAmountInclTax,
    type,
  }

  ${priceRuleFragment}
  ${paymentFragment}
  ${invoiceFragment}
`;

/* ----------------------- */
/* Purchase for my booking */
/* ----------------------- */
const purchaseForMyBookingFragment = `
  fragment purchaseForMyBookingFragment on Purchase {
    amountExclTax,
    amountInclTax,
    discount,
    discountedAmountExclTax,
    discountedAmountInclTax,
    id,
    offers {
      amountExclTax
      amountInclTax
      begin,
      discount
      discountedAmountExclTax
      discountedAmountInclTax
      end,
      expiresAt,
      priceRule {
        ...priceRuleFragment
      }
      priceType,
      reference,
      serviceFeeAmountExclTax
      serviceFeeAmountInclTax
      spacesQuantity,
      status,
      totalAmountExclTax
      totalAmountInclTax
      type,
    }
    paidAmount,
    paymentInvoice {
      ...invoiceFragment
    },
    paymentStatus,
    payments {
      ...paymentFragment
    },
    priceRule {
      ...priceRuleFragment
    },
    serviceFeeAmountExclTax,
    serviceFeeAmountInclTax,
    totalAmountExclTax,
    totalAmountInclTax,
    type,
  }

  ${priceRuleFragment}
  ${paymentFragment}
  ${invoiceFragment}
`;
// 🔽
/* --------------------------- */
/* Purchases(!) for my booking */
/* --------------------------- */
export const purchasesForMyBookingFragment = `
  fragment purchasesForMyBookingFragment on Booking {
    purchases {
      ...purchaseForMyBookingFragment
    }
  }

  ${purchaseForMyBookingFragment}
`;

/* ---------------------------------------------------------------------------- */
/*                                   CARPARK                                    */
/* ---------------------------------------------------------------------------- */

/* ------- */
/* Address */
/* ------- */
export const addressFragment = `
fragment addressFragment on Address {
  city,
  country,
  extra,
  street,
  zipCode
}
`;

/* -------- */
/* Location */
/* -------- */
export const locationFragment = `
  fragment locationFragment on Location {
    latitude,
    longitude
  }
`;

/* ------- */
/* Carpark */
/* ------- */
/* eslint-disable no-use-before-define */
export const carparkFragment = (
  {
    withEquipmentsRead,
    withConfiguration,
    withLicensePlateConfiguration,
    withBookings,
    withAdmin,
    withConfigurationSensitiveRead,
  },
  options = null,
) => `
  fragment carparkFragment on Carpark {
    address {
      ...addressFragment
    },
    allowListingOfOneparkBookings,
    capacity,
    contact { email, phone }
    devices {
      ...deviceFragment
    },
    doors {
      ...doorFragment
    },
    ${withEquipmentsRead ? (`
      terminals {
        ...terminalFragment
      },
    `) : ''}
    ${(withAdmin || withConfigurationSensitiveRead) ? (`
      financialInformation {
        bookingCommission,
        bookingServiceFeeRule { ...serviceFeeRuleFragment }
        invoiceIdentifier,
        onDemandCommission,
        onDemandServiceFeeRule { ...serviceFeeRuleFragment }
        paymentFrequency,
      },
    `) : ''}
    id,
    location {
      ...locationFragment
    },
    name,
    publicName,
    ${withAdmin ? (`
      partner {
        ...partnerFragment
      },
    `) : ''}
    permissions,
    ${withConfiguration || withBookings ? (`
      settings {
        booking {
          defaultParkingCategory {
            ...defaultParkingCategoryFragment
          }
          entryCourtesyDelay,
          exitCourtesyDelay,
          resetDelay,
          maxDate,
          maxDuration,
          moduleEnabled,
        },
        general {
          address {
            ...addressFragment
          }
          email
          entryByLicensePlateReader
          exitByLicensePlateReader
          forbidManualKeypadCodes
          location {
            latitude
            longitude
          }
          phone
          maximumHeight
          accessSchedule
          authorizedVehicles
          allowLpg
          chargingStation
        }
        ${withConfiguration ? (`
          onDemand {
            configuration {
              __typename,
              ... on CarparkOnDemandAutoCategorizationConfiguration {
                parkingCategory {
                  id
                }
              }
              ... on CarparkOnDemandSpecificCategoryConfiguration {
                quickParkingCategory {
                  id
                },
              }
            },
            confirmationKeypadCode,
            enabled,
            capacityCheck,
            exitCourtesyDelay,
            dropOffDuration,
            temporaryExitMaxDuration,
            temporaryExitResetDelay,
            ignoredLicensePlates,
            mode,
          },
        `) : ''}
        ${withConfiguration ? (`
          spaces {
            bookingCapacity,
            assignedCapacity,
          },
        `) : ''}
        ${withLicensePlateConfiguration ? (`
          licensePlate {
            recordCooldownEntryAfterEntry,
            recordCooldownExitAfterExit,
            recordCooldownExitAfterEntry,
            recordCooldownEntryAfterExit,
          }
        `) : ''}
      },
    `) : ''}
    slug {
      id
      domain
      slug
    },
    timezone,
    ${(withAdmin || withConfigurationSensitiveRead) ? (`
      workspaceGroups
      ${options ? options.workspaceGroupsSearch : ''}
      {
        entries {
          ...workspaceGroupFragment
        },
      }
      workspaceId
    `) : ''}
  }

  ${withConfiguration || withBookings ? defaultParkingCategoryFragment : ''}
  ${addressFragment}
  ${deviceFragment}
  ${doorFragment}
  ${withEquipmentsRead ? terminalFragment : ''}
  ${locationFragment}
  ${(withAdmin || withConfigurationSensitiveRead) ? workspaceGroupFragment : ''}
  ${withAdmin ? partnerFragment : ''}
  ${(withAdmin || withConfigurationSensitiveRead) ? serviceFeeRuleFragment : ''}
`;

export const carparkActionBarInformationFragment = `
  fragment carparkActionBarInformationFragment on Carpark {
      name,
      address {
        city,
        country,
        extra,
        street,
        zipCode
      },
      capacity,
      contact { email, phone }
      terminals {
        paymentReaders {
          connectionStatus
        }
        timeout
      }
      synchronizationStatuses {
        connectedEquipmentsTimeoutStatus
      }
  }
`;
/* eslint-enable no-use-before-define */

/* ---------------- */
/* Carpark for list */
/* ---------------- */
export const carparkForListFragment = `
  fragment carparkForListFragment on Carpark {
    address {
      ...addressFragment
    },
    id,
    location {
      ...locationFragment
    },
    name,
    publicName,
    permissions,
  }

  ${addressFragment}
  ${locationFragment}
`;

/* --------------------------------- */
/* Carpark for service fee rule list */
/* --------------------------------- */
export const carparkForServiceFeeListFragment = `
  fragment carparkForServiceFeeListFragment on Carpark {
    id
    name
    address {
      city
      country
      street
      zipCode
    }
  }
`;

/* ----------------------------- */
/* Carpark with booking settings */
/* ----------------------------- */
export const carparkForBookingFragment = `
  fragment carparkForBookingFragment on Carpark {
    address {
      ...addressFragment
    },
    id,
    location {
      ...locationFragment
    },
    name,
    publicName,
    permissions,
    settings {
      booking {
        entryCourtesyDelay,
        exitCourtesyDelay,
        maxDate,
        maxDuration,
        moduleEnabled,
      },
      general {
        address {
          ...addressFragment
        }
        email
        location {
          latitude
          longitude
        }
        phone
        maximumHeight
        accessSchedule
        authorizedVehicles
        allowLpg
        chargingStation
      }
    },
    timezone
  }

  ${addressFragment}
  ${locationFragment}
`;

/* -------------------- */
/* Carpark default slug */
/* -------------------- */
export const carparkDefaultSlugFragment = `
  fragment carparkDefaultSlugFragment on Carpark {
    slug {
      id,
      domain,
      slug
    }
  }
`;

/* ----------------------- */
/* Carpark synchronization */
/* ----------------------- */
export const carparkSynchronizationFragment = `
  fragment carparkSynchronizationFragment on Carpark {
    synchronizationStatuses {
      bookings,
      onDemandAccesses,
      parkings,
      parkingCategories,
      plannings
    }
  }
`;

/* ---------------------- */
/* Carpark booking option */
/* ---------------------- */
export const carparkBookingOptionFragment = `
  fragment carparkBookingOptionFragment on CarparkBookingOption {
    customerIds,
    parkingCategoryId,
    priceGrid {
      id,
      additional { amountInclTax },
      name,
      priceRule {
        ...priceRuleFragment
      }
      tiers {
        amountInclTax
        duration,
        unit,
      }
    }
  }

  ${priceRuleFragment}
`;

/* ---------------------------------------------------------------------------- */
/*                                   PARKING                                    */
/* ---------------------------------------------------------------------------- */

/* ------------ */
/* Live Carpark */
/* ------------ */
export const liveCarparkFragment = `
  fragment GetLiveCarparkData on Parking {
    id
    functionalType
    spacesQuantity
    state {
      insideQuantity
      outsideQuantity
    }
    vehicles {
      licensePlate
    }
    customer {
      firstname
      lastname
      parkingCategory {
        name
        color
        id
      }
    }
    parkingCategory {
        name
        color
        id
    }
    purchases {
      paymentStatus
    }
    spacesQuantity
    begin
    end
  }
`;

/* ------- */
/* Parking */
/* ------- */
export const parkingFragment = ({ withCustomer, withParkingCategory, withPlanning }) => `
  fragment parkingFragment on Parking {
    begin,
    comments,
  ${withCustomer ? (`
    customer {
      email,
      firstname,
      id,
      lastname,
    ${withParkingCategory ? (`
      parkingCategory {
        id,
        type
      },
    `) : ''}
      phone,
      spaceNumber
    },
  `) : ''}
    doors {
      id
    },
    end,
    id,
    creationDate,
    keypadCodes,
  ${withParkingCategory ? (`
    parkingCategory {
      id
    },
  `) : ''}
  ${withPlanning ? (`
    planning {
      id
    },
  `) : ''}
    purchases {
      ...purchaseFragment
    },
    reference,
    restrictedByAntipassbackEntrance,
    restrictedByAntipassbackExit,
    restrictedByForbiddenEntry,
    restrictedByForbiddenExit,
    restrictedByPermanentExit,
    spacesQuantity,
    state {
      insideQuantity,
      outsideQuantity,
      outsideQuantityPermanentExit,
      unknownQuantity
    },
    status,
    functionalType,
    type,
    vehicles {
      licensePlate
    }
  }
  ${purchaseFragment}
`;

/* ---------------------------------------------------------------------------- */
/*                       PARKING CATEGORY AND ALLOTMENT                         */
/* ---------------------------------------------------------------------------- */

/* -------------------------- */
/* Parking category allotment */
/* -------------------------- */
export const parkingCategoryAllotmentFragment = `
  fragment parkingCategoryAllotmentFragment on DefaultAllotment {
    assignedCapacities {
      max,
      min
    }
    bookingCapacities {
      max,
      min
    },
    id,
    withoutBookingCapacities {
      max,
      min
    }
  }
`;

/* ---------------- */
/* Parking category */
/* ---------------- */
export const parkingCategoryFragment = `
  fragment parkingCategoryFragment on ParkingCategory {
    color,
    id,
    isOnepark,
    name,
    priceGridAssociation {
      discount,
      calculationMode,
      priceGrid {
        id
      }
    },
    type,
    defaultAllotment {
      ...parkingCategoryAllotmentFragment
    },
  }

  ${parkingCategoryAllotmentFragment}
  `;

export const defaultParkingCategoryFragment = `
  fragment defaultParkingCategoryFragment on ParkingCategory {
    id,
    color,
    name
  }
`;

export const parkingCategoryForDashboardFragment = `
  fragment parkingCategoryForDashboardFragment on ParkingCategory {
    id,
    name,
    type
  }`;

export const parkingCategoryForOnDemandAccessesFragment = `
  fragment parkingCategoryForOnDemandAccessesFragment on ParkingCategory {
    color,
    id,
    isOnepark,
    name,
  }`;

/* ---------------------------------------------------------------------------- */
/*                                   BOOKING                                    */
/* ---------------------------------------------------------------------------- */

/* ------- */
/* Booking */
/* ------- */
export const bookingFragment = ({ withCustomer, withParkingCategory }) => `
  fragment bookingFragment on Booking {
    id,
    begin,
    carpark {
      id,
      name,
      timezone,
    },
    comments,
    ${withCustomer ? (`
      customer {
        id,
        email,
        phone,
        firstname,
        lastname,
        ${withParkingCategory ? (`
          parkingCategory {
            id
          }
        `) : ''}
      },
    `) : ''}
    doors {
      id
    },
    end,
    creationDate,
    keypadCodes,
    ${withParkingCategory ? (`
      parkingCategory {
        id,
        name,
      },
    `) : ''}
    purchases {
      ...purchaseFragment
    },
    isRefundable,
    reference,
    restrictedByAntipassbackEntrance,
    restrictedByAntipassbackExit,
    restrictedByForbiddenEntry,
    restrictedByForbiddenExit,
    restrictedByPermanentExit,
    spacesQuantity,
    state {
      insideQuantity,
      outsideQuantity,
      outsideQuantityPermanentExit,
      unknownQuantity
    },
    status,
    vehicles {
      licensePlate
    }
  }

  ${purchaseFragment}
`;

/* --------------- */
/* Onepark booking */
/* --------------- */
export const oneparkBookingFragment = `
  fragment oneparkBookingFragment on OneparkBooking {
    begin,
    bookingId,
    bookingUsage,
    customerFirstname,
    customerLastname,
    end,
    keypadCode,
    status,
    licensePlate,
  }
`;

export const onDemandAccessFragment = `
  fragment onDemandAccessFragment on OnDemandAccess {
    active,
    begin,
    creationDate,
    customer {
      id,
      parkingCategory {
        id,
        type,
        name,
        color,
      },
    }
    email,
    end,
    keypadCode,
    id,
    licensePlate,
    manualCreation,
    isRefundable,
    parkingCategory {
      id,
      type,
    },
    priceGrid {
      ...priceGridFragment
    },
    purchases {
      ...purchaseFragment
    },
    isDeletable,
    isRefundable,
    readLicensePlate,
    spacesQuantity,
    reference,
    restrictedByAntipassbackEntrance,
    restrictedByAntipassbackExit,
    restrictedByForbiddenEntry,
    restrictedByForbiddenExit,
    restrictedByPermanentExit,
    selfCheckoutType,
    status,
    state {
      insideQuantity,
      outsideQuantity,
      outsideQuantityPermanentExit,
      unknownQuantity
    },
  }

  ${paymentFragment}
  ${priceGridFragment}
  ${purchaseFragment}
`;

/* ------ */
/* Access */
/* ------ */
export const accessFragment = ({
  withParking,
  withCustomer,
  withBooking,
  withParkingCategory,
}) => `
  fragment accessFragment on Access {
    date,
    door {
      ...doorFragment
    },
    ${withBooking ? (`
    booking {
      customer {
        firstname,
        id,
        lastname,
        parkingCategory {
          id,
        }
      }
    }
    onDemandAccess {
      customer {
        firstname,
        id,
        lastname,
        parkingCategory {
          id,
        }
      }
    }
    `) : ''}
    equipment {
      ...equipmentFragment
    },
    id,
    ${withBooking ? (`
      oneparkBooking {
      ...oneparkBookingFragment
    },`) : ''}
    ${withParking ? (`
      parking {
        begin,
      ${withCustomer ? (`
        customer {
          firstname,
          id,
          lastname,
        ${withParkingCategory ? (`
          parkingCategory {
            id
          },
        `) : ''}
          spaceNumber
        },
      `) : ''}
        end,
        id,
      ${withParkingCategory ? (`
        parkingCategory {
          id
        },
      `) : ''}
      reference,
      spacesQuantity,
      state {
        insideQuantity,
        outsideQuantity,
        outsideQuantityPermanentExit,
        unknownQuantity
      }
    },
  `) : ''}
    result,
    source,
    type,
    usage
  }

  ${doorFragment}
  ${equipmentFragment}
  ${withBooking ? oneparkBookingFragment : ''}
`;

/* ---------- */
/* My booking */
/* ---------- */
export const myBookingFragment = `
  fragment myBookingFragment on Booking {
    id,
    begin,
    carpark {
      id,
      name,
      publicName,
      settings {
        general {
          address {
            ...addressFragment
          }
          email
          entryByLicensePlateReader
          exitByLicensePlateReader
          location {
            latitude
            longitude
          }
          phone
        }
        booking {
          exitCourtesyDelay,
          moduleEnabled
        },
      },
      timezone,
    },
    end,
    keypadCodes,
    purchases {
      ...purchaseForMyBookingFragment
    },
    reference,
    spacesQuantity,
    status,
    vehicles {
      licensePlate
    }
  }

  ${addressFragment}
  ${purchaseForMyBookingFragment}
`;

/* ---------- */
/* My booking */
/* ---------- */
export const myBookingForListFragment = `
  fragment myBookingForListFragment on Booking {
    id,
    begin,
    carpark {
      id,
      name,
      publicName,
      timezone,
    },
    end,
    keypadCodes,
    purchases {
      ...purchaseForMyBookingFragment
    },
    reference,
    spacesQuantity,
    status,
    vehicles {
      licensePlate
    }
  }

  ${purchaseForMyBookingFragment}
`;

/* ------------------------------ */
/* My booking with purchases only */
/* ------------------------------ */
export const myBookingWithPurchasesOnlyFragment = `
  fragment myBookingWithPurchasesOnlyFragment on Booking {
    id,
    purchases {
      ...purchaseForMyBookingFragment
    },
    reference,
  }

  ${purchaseForMyBookingFragment}
`;

/* ---------------------------------------------------------------------------- */
/*                                ON DEMAND                                     */
/* ---------------------------------------------------------------------------- */

/* --------- */
/* On Demand */
/* --------- */

/* ---------------------- */
/* On Demand for checkout */
/* ---------------------- */
export const onDemandAccessForCheckoutFragment = `
  fragment onDemandAccessForCheckoutFragment on OnDemandAccess {
    active,
    carpark {
      address {
        city,
        country,
        extra,
        street,
        zipCode
      }
      contact {
        email,
        phone
      }
      location {
        latitude,
        longitude
      },
      entryByLicensePlateReader,
      exitByLicensePlateReader,
      id,
      onDemandTemporaryExitMaxDuration
      publicName,
      onDemandExitCourtesyDelay,
      slug {
        domain,
        id,
        slug,
      },
    },
    begin,
    email,
    end,
    keypadCode,
    id,
    licensePlate,
    magicLink,
    priceGrid {
      ...priceGridFragment
    },
    purchases {
      ...purchaseForCheckoutFragment
    },
    reference,
    selfCheckoutType,
    status,
  }

  ${paymentFragment}
  ${priceGridFragment}
  ${purchaseForCheckoutFragment}
`;

/* ---------------------------------------------------------------------------- */
/*                                CUSTOMER                                      */
/* ---------------------------------------------------------------------------- */

/* -------- */
/* Customer */
/* -------- */
export const customerFragment = ({
  withBookings, withParkings, withParkingCategory, withPlanning,
}, { inProgress, started }) => `
  fragment customerFragment on Customer {
    account {
      status,
    },
    ${withBookings ? (`
      bookings
      (
        search:
          {
            and: [
              {
                and: [
                  {
                    active: {
                      eq: true
                    }
                  }
                ]
              },
              {
                or: [
                  {
                    status: {
                      eq: ${inProgress}
                    }
                  },
                  {
                    status: {
                      eq: ${started}
                    }
                  }
                ]
              }
            ]
          },
        sort:
          {
            begin : DESC
          }
      )
      {
        ...bookingFragment
      },
    `) : ''}
    comments,
    email,
    firstname,
    id,
    lastname,
    ${withParkings ? (`
      parkings
        (search:
          {
            and: [
              {
                and: [
                  {
                    active: {
                      eq: true
                    }
                  }
                ]
              },
              {
                or: [
                  {
                    status: {
                      eq: ${inProgress}
                    }
                  },
                  {
                    status: {
                      eq: ${started}
                    }
                  }
                ]
              }
            ]
          },
          sort:
            {
              begin : DESC
            }
        )
      {
        ...parkingFragment
      },
    `) : ''}
  ${withParkingCategory ? (`
    parkingCategory {
      id,
      type
    },
  `) : ''}
    phone,
    spaceNumber,
  }

  ${withBookings ? `${bookingFragment({ withParkingCategory })}` : ''}
  ${withParkings ? `${parkingFragment({ withParkingCategory, withPlanning })}` : ''}
`;

/* ---------------------------------------------------------------------------- */
/*                               PLANNING                                       */
/* ---------------------------------------------------------------------------- */

/* -------- */
/* Planning */
/* -------- */
export const planningFragment = `
  fragment planningFragment on Planning {
    active,
    enabled,
    id,
    name,
    slots {
      end {
        dayOfWeek,
        time
      },
      start {
        dayOfWeek,
        time
      }
    }
  }
`;

/* ---------------------------------------------------------------------------- */
/*                               ACCOUNT                                        */
/* ---------------------------------------------------------------------------- */

/* ------- */
/* Account */
/* ------- */
export const accountFragment = `
  fragment accountFragment on Account {
    id
    authorizations {
      id
      carpark {
        id,
        name,
        publicName
      }
      cause
      history {
        cause
        comments
        date
        status
      }
      status
    }
    email
    firstname
    history {
      cause
      comments
      date
      status
    }
    lastname
  }
`;

export const accountFragmentForGlobalList = `
  fragment accountFragmentForGlobalList on Account {
    id
    firstname
    lastname
    email
    phone
    invoiceAddress {
      city
      country
      extra
      street
      zipCode
    }
    isArchivable
    isDeletable
    totalAccesses
    activeAccesses
    carparks {
      name
      id
    }
    status
  }
`;

/* ---------------------------------------------------------------------------- */
/*                                SLUG                                          */
/* ---------------------------------------------------------------------------- */

/* ---- */
/* Slug */
/* ---- */
export const slugFragment = ({ withAdmin }, withToken = true) => `
  fragment slugFragment on Slug {
  ${withAdmin && withToken ? `
    comments,
    type,
    usedOnPrintedQrCode,
    workspace {
      id,
      name
    },
    workspaceGroup {
      id,
      name
    },
  `
    : ''
}
    customization {
      hideHeaderLogo,
      logo,
      showOnDemandCheckoutTutorial,
      title,
    },
    domain,
    id,
    slug,
    usages,
  }
`;

/* ---------------------------------------------------------------------------- */
/*                         WORKSPACES AND PARTNERS                              */
/* ---------------------------------------------------------------------------- */

/* ----------------------------- */
/* Partner financial information */
/* ----------------------------- */
const partnerFinancialInformationFragment = `
  fragment partnerFinancialInformationFragment on PartnerFinancialInformation {
    address {
      ...addressFragment
    },
    emailForCommissionInvoices,
    europeanVatNumber,
    iban,
    invoiceIdentifier,
    nationalIdentifier,
  }
  ${addressFragment}
`;

/* ------- */
/* Partner */
/* ------- */
export const partnerFragment = `
  fragment partnerFragment on Partner {
    financialInformation {
      ...partnerFinancialInformationFragment
    },
    id,
    name,
  }
  ${partnerFinancialInformationFragment}
`;

/* --------- */
/* Workspace */
/* --------- */
export const workspaceFragment = `
  fragment workspaceFragment on Workspace {
    id,
    name,
  }
`;

/* --------------- */
/* Workspace group */
/* --------------- */
export const workspaceGroupFragment = `
  fragment workspaceGroupFragment on WorkspaceGroup {
    id,
    name,
    slugs {
      entries {
        id,
        domain,
        slug,
        usedOnPrintedQrCode,
        comments,
        usages
      }
    }
    type,
    workspace {
      id,
      name
    },
  }
`;



// WEBPACK FOOTER //
// ./models/cpm/fragments/index.js